/**
 * API 정의.
 *
 * 다른 시스템을 붙이려는 사람이 콘솔 안에서 계약을 읽는 화면이다. 저장소의
 * `docs/PUBLIC-API.md` 를 열지 않아도, 무엇을 어떤 권한으로 부르는지와 **붙여 넣을
 * 수 있는 호출 한 줄**이 여기 있다.
 *
 * **엔드포인트 목록을 콘솔에 다시 쓰지 않는다.** 서버가 내는 OpenAPI 문서
 * (`/api/v1/public/openapi.json`)를 그대로 그린다. 손으로 적어 두면 서버가 하나
 * 늘릴 때 콘솔은 모르고, 그 사실이 아무 데서도 드러나지 않는다 — 화면만 조용히
 * 낡는다. 서버 쪽은 엔드포인트 표 하나에서 라우트와 스펙이 함께 나오므로,
 * 여기까지 이어 놓으면 **계약이 세 곳에서 같은 한 벌**이 된다.
 *
 * 그래서 이 파일에는 API 지식이 없다. 있는 것은 스펙을 사람이 읽을 모양으로
 * 바꾸는 규칙뿐이고, 그 규칙들은 순수 함수로 빼서 시험으로 고정했다.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, Download, KeyRound, Plug, ShieldCheck } from 'lucide-react';
import type {
  OpenApiDocument,
  OpenApiOperation,
  OpenApiParameter,
  OpenApiSchema,
  SignageOneClient,
} from '../api/client.js';
import { useAsync } from '../hooks.js';
import { Badge, CommandBox, ErrorNote, Loading, Panel } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { Markdown } from '../components/markdown.js';

/* ────────────────────────── 스펙을 읽는 규칙 ────────────────────────── */

export interface Endpoint {
  key: string;
  method: string;
  path: string;
  operation: OpenApiOperation;
  /** 묶음 이름. 경로에서 뽑는다. */
  group: string;
}

/**
 * 경로에서 묶음 이름을 뽑는다.
 *
 * 스펙에 `tags` 를 넣어 서버가 정해 줄 수도 있지만, 그러면 묶음 이름이 **두 곳**
 * (서버의 태그와 콘솔의 번역)에 생긴다. 경로가 이미 묶음을 말하고 있으므로 그것을 읽는다.
 */
const GROUP_LABEL: Readonly<Record<string, string>> = {
  displays: '화면',
  content: '콘텐츠',
  announcements: '알림',
  emergencies: '긴급',
  reports: '리포트',
  'openapi.json': '계약',
};

export function groupOf(path: string): string {
  const rest = path.replace(/^\/api\/v1\/public\/?/, '');
  const head = rest.split('/')[0] ?? '';
  return GROUP_LABEL[head] ?? head ?? '기타';
}

/**
 * 문서를 화면에 그릴 목록으로 편다.
 *
 * **스펙에 적힌 순서를 지킨다.** 서버의 엔드포인트 표는 읽는 순서(조회 → 게시 →
 * 해제)로 놓여 있고, 여기서 알파벳순으로 다시 정렬하면 그 의도가 사라진다.
 */
export function endpointsOf(doc: OpenApiDocument): Endpoint[] {
  const out: Endpoint[] = [];
  for (const [path, operations] of Object.entries(doc.paths ?? {})) {
    for (const [method, operation] of Object.entries(operations)) {
      out.push({
        key: `${method}:${path}`,
        method: method.toUpperCase(),
        path,
        operation,
        group: groupOf(path),
      });
    }
  }
  return out;
}

/** 묶음 순서도 스펙에 처음 나온 순서다. */
export function groupsOf(endpoints: readonly Endpoint[]): Array<{ title: string; items: Endpoint[] }> {
  const groups: Array<{ title: string; items: Endpoint[] }> = [];
  for (const endpoint of endpoints) {
    const found = groups.find((group) => group.title === endpoint.group);
    if (found) found.items.push(endpoint);
    else groups.push({ title: endpoint.group, items: [endpoint] });
  }
  return groups;
}

/**
 * 스키마 한 줄의 타입 표기.
 *
 * `string`·`integer` 만 보여주면 읽는 사람이 값을 못 고른다. 열거형은 **고를 수 있는
 * 값 자체가 타입**이고, 배열은 무엇의 배열인지가 타입이다.
 */
export function typeLabel(schema: OpenApiSchema | undefined): string {
  if (!schema) return '—';
  if (schema.$ref) return schema.$ref.split('/').at(-1) ?? 'object';
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return schema.enum.map((value) => String(value)).join(' | ');
  }
  if (schema.type === 'array') return `${typeLabel(schema.items)}[]`;
  if (schema.format === 'date-time') return '시각(ISO)';
  // ObjectId 는 우리 쪽 개념이라 `string` 보다 이렇게 적는 편이 알아보기 쉽다.
  if (schema.pattern === '^[0-9a-f]{24}$') return 'id';
  return schema.type ?? 'any';
}

/**
 * 값의 제약. 표의 좁은 칸에 들어가야 하므로 **읽는 사람이 실제로 걸리는 것**만 남긴다.
 * 정규식 원문 같은 것은 넣지 않는다 — 칸을 다 먹으면서 아무도 읽지 않는다.
 */
export function constraintOf(schema: OpenApiSchema | undefined): string {
  if (!schema) return '';
  const parts: string[] = [];
  if (schema.default !== undefined) parts.push(`기본 ${String(schema.default)}`);
  if (schema.minimum !== undefined || schema.maximum !== undefined) {
    parts.push(`${schema.minimum ?? ''}~${schema.maximum ?? ''}`);
  }
  if (schema.maxLength !== undefined) parts.push(`최대 ${schema.maxLength}자`);
  if (schema.maxItems !== undefined) parts.push(`최대 ${schema.maxItems}개`);
  if (schema.nullable) parts.push('null 가능');
  return parts.join(' · ');
}

export interface Field {
  name: string;
  type: string;
  required: boolean;
  description: string;
  constraint: string;
}

/**
 * 객체 스키마를 표의 줄들로 편다.
 *
 * **한 겹만 편다.** 중첩까지 모두 펴면 응답 하나가 수십 줄이 되어 훑을 수 없다.
 * 중첩된 것은 타입 표기(`Display[]` 같은)로 형태를 알리고, 정확한 것은 스펙 원문을
 * 내려받아 보게 한다.
 */
export function fieldsOf(schema: OpenApiSchema | undefined): Field[] {
  const properties = schema?.properties;
  if (!properties) return [];
  const required = new Set(schema?.required ?? []);
  return Object.entries(properties).map(([name, value]) => ({
    name,
    type: typeLabel(value),
    required: required.has(name),
    description: value.description ?? '',
    constraint: constraintOf(value),
  }));
}

/** 스키마에서 본문 예시를 만든다. 있는 것만 채운다 — 없는 값을 지어내면 그것이 계약처럼 읽힌다. */
function sampleValue(schema: OpenApiSchema | undefined): unknown {
  if (!schema) return null;
  // 서버가 예시를 줬으면 그것이 이긴다. 스키마에서 뽑아낸 것보다 언제나 정확하다.
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];
  if (schema.$ref) return {};
  switch (schema.type) {
    case 'array':
      return [sampleValue(schema.items)];
    case 'object':
      return sampleObject(schema);
    case 'integer':
    case 'number':
      return schema.minimum ?? 1;
    case 'boolean':
      return true;
    default:
      if (schema.format === 'date-time') return '2026-06-15T09:00:00Z';
      if (schema.pattern === '^[0-9a-f]{24}$') return '<id>';
      return '…';
  }
}

/**
 * 예시 본문.
 *
 * **필수 필드만 넣는다.** 선택 필드까지 채우면 붙여 넣은 사람이 그것을 다 보내야
 * 하는 줄 알고, 지우는 대신 값을 채우려다 막힌다. 필수가 하나도 없으면 첫 필드
 * 하나만 보여 형태를 알린다.
 */
export function sampleObject(schema: OpenApiSchema | undefined): Record<string, unknown> {
  /*
   * 서버가 통째로 준 예시가 있으면 그것을 쓴다.
   *
   * `required` 만으로 설명되지 않는 호출이 있다 — 두 필드가 서로 대안이면 어느
   * 쪽도 필수가 아니고, 그러면 뽑아낸 예시가 400 나는 본문이 된다. 처음 보내 본
   * 것이 실패하면 그다음부터는 이 화면을 안 믿는다.
   */
  if (schema?.example && typeof schema.example === 'object' && !Array.isArray(schema.example)) {
    return schema.example as Record<string, unknown>;
  }
  const properties = schema?.properties;
  if (!properties) return {};
  const required = schema?.required ?? [];
  const names = required.length > 0 ? required : Object.keys(properties).slice(0, 1);
  const out: Record<string, unknown> = {};
  for (const name of names) {
    const child = properties[name];
    if (child) out[name] = sampleValue(child);
  }
  return out;
}

/**
 * 붙여 넣을 수 있는 호출 한 줄.
 *
 * 이 화면의 값어치는 대부분 여기 있다. 표를 읽고 손으로 curl 을 조립하는 동안
 * 사람은 헤더 이름과 따옴표에서 틀리고, 그 실패는 "권한 문제인가" 로 오해된다.
 *
 * 경로 파라미터는 `<id>` 로 남긴다 — 그럴듯한 가짜 id 를 넣으면 그대로 보내 보고
 * 404 를 받는다.
 */
export function curlFor(endpoint: Endpoint, server: string): string {
  const url = `${server}${endpoint.path}`.replace(/\{([^}]+)\}/g, '<$1>');
  const needsAuth = Boolean(endpoint.operation['x-capability']);

  const required = (endpoint.operation.parameters ?? []).filter(
    (parameter) => parameter.in === 'query' && parameter.required,
  );
  const queryString = required
    .map((parameter) => `${parameter.name}=${String(sampleValue(parameter.schema) ?? '')}`)
    .join('&');

  const lines: string[] = [];
  const target = queryString ? `"${url}?${queryString}"` : `"${url}"`;
  lines.push(endpoint.method === 'GET' ? `curl ${target}` : `curl -X ${endpoint.method} ${target}`);
  if (needsAuth) lines.push(`  -H "authorization: Bearer $SIGNAGEONE_TOKEN"`);

  const body = endpoint.operation.requestBody?.content?.['application/json']?.schema;
  if (body) {
    lines.push(`  -H 'content-type: application/json'`);
    lines.push(`  -d '${JSON.stringify(sampleObject(body), null, 2)}'`);
  }

  return lines.join(' \\\n');
}

/** 200 응답의 스키마. 여러 콘텐츠 타입을 두지 않으므로 JSON 하나만 본다. */
export function responseSchemaOf(operation: OpenApiOperation): OpenApiSchema | undefined {
  return operation.responses?.['200']?.content?.['application/json']?.schema;
}

/** 능력별로 그것을 가진 최소 역할. 토큰을 어떤 역할로 발급할지 고르는 근거다. */
const CAPABILITY_ROLE: Readonly<Record<string, string>> = {
  read: 'viewer',
  'content:write': 'content-manager',
  'schedule:write': 'admin',
  'fleet:write': 'admin',
  'report:read': 'auditor',
  'audit:read': 'auditor',
  'user:manage': 'admin',
  'settings:write': 'admin',
  'emergency:write': 'admin',
  system: 'system',
  'plugin:manage': 'system',
};

/**
 * 이 API 를 쓰려면 어떤 역할의 토큰이 필요한가.
 *
 * 스펙에 있는 능력들에서 거꾸로 계산한다 — 화면에 역할 표를 손으로 적어 두면
 * 서버가 능력을 바꿔도 그대로 남는다.
 */
export function rolesNeeded(endpoints: readonly Endpoint[]): Array<{ capability: string; role: string; count: number }> {
  const counts = new Map<string, number>();
  for (const endpoint of endpoints) {
    const capability = endpoint.operation['x-capability'];
    if (!capability) continue;
    counts.set(capability, (counts.get(capability) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([capability, count]) => ({ capability, role: CAPABILITY_ROLE[capability] ?? capability, count }))
    .sort((a, b) => b.count - a.count);
}

/* ────────────────────────────── 화면 ────────────────────────────── */

const METHOD_TONE: Readonly<Record<string, 'ok' | 'brand' | 'danger' | 'neutral'>> = {
  GET: 'ok',
  POST: 'brand',
  DELETE: 'danger',
};

export function ApiSpecScreen({ client }: { client: SignageOneClient }): ReactNode {
  const spec = useAsync(() => client.openApiSpec(), [client]);
  const doc = spec.data;

  const endpoints = useMemo(() => (doc ? endpointsOf(doc) : []), [doc]);
  const groups = useMemo(() => groupsOf(endpoints), [endpoints]);
  const roles = useMemo(() => rolesNeeded(endpoints), [endpoints]);

  /*
   * 서버 주소는 스펙이 알려 준 것을 쓴다. 콘솔이 자기 주소를 넣으면 iframe·호스트
   * 브리지 배포에서 틀린 주소가 나간다 — 그 배포에서 콘솔은 서버가 어디 있는지 모른다.
   */
  const server = doc?.servers?.[0]?.url ?? 'https://<서버 주소>';

  if (spec.loading) return <Loading />;
  if (spec.error || !doc) {
    return (
      <ErrorNote
        message={`API 정의를 불러오지 못했습니다 — ${spec.error ?? '응답이 비어 있습니다'}`}
        onDismiss={spec.reload}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* 표지 — 이 API 가 무엇이고 콘솔 API 와 무엇이 다른지 먼저 말한다. */}
      <section
        className="relative overflow-hidden rounded-xl px-5 py-7 sm:px-8 sm:py-9"
        style={{ background: 'linear-gradient(135deg, rgb(var(--th-3)) 0%, rgb(var(--th-3) / 0.88) 100%)' }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgb(var(--th-5) / 0.18) 0%, transparent 70%)' }}
        />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11.5px] font-medium tracking-wide text-th-6">
            <Plug className="h-3.5 w-3.5" strokeWidth={2} />
            통합 API v{doc.info.version}
          </span>
          <h1 className="mt-4 text-[28px] font-semibold leading-tight tracking-tight text-white">
            {doc.info.title}
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed text-slate-300">
            다른 업무 시스템이 SignageOne 을 다루기 위한 API 입니다. 콘텐츠·플레이리스트·배정의
            구조를 배우지 않아도 <strong className="font-semibold text-white">한 번의 호출</strong>로
            화면에 알림을 띄우고, 지금 무엇이 나가는지 확인할 수 있습니다.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <code className="rounded-md bg-black/25 px-3 py-1.5 font-mono text-[12.5px] text-slate-200">
              {server}/api/v1/public
            </code>
            <span className="text-[12.5px] text-slate-400">엔드포인트 {endpoints.length}개</span>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          {groups.map((group) => (
            <Panel key={group.title} title={group.title} subtitle={`${group.items.length}개`}>
              <ul className="divide-y divide-slate-100">
                {group.items.map((endpoint) => (
                  <EndpointRow key={endpoint.key} endpoint={endpoint} server={server} />
                ))}
              </ul>
            </Panel>
          ))}
        </div>

        <aside className="space-y-5">
          <Panel title="시작하기">
            <div className="space-y-4 p-4">
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
                  <KeyRound className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
                  ① 서비스 토큰 발급
                </p>
                <CommandBox
                  command={`curl -X POST "${server}/api/v1/admin/tokens" \\\n  -H "authorization: Bearer $ADMIN_TOKEN" \\\n  -H 'content-type: application/json' \\\n  -d '{"name":"my-system","role":"admin"}'`}
                />
                <p className="mt-2 text-[12px] leading-relaxed text-slate-500">
                  평문 토큰은 <strong className="font-semibold text-slate-700">이 응답에서 한 번만</strong>{' '}
                  표시됩니다. 잃으면 새로 발급해야 합니다.
                </p>
              </div>

              <div>
                <p className="mb-2 flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
                  <Download className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />
                  ② 클라이언트 생성 (선택)
                  <HelpTip
                    text="이 화면과 같은 OpenAPI 3.1 문서입니다. 인증 없이 받을 수 있고, 코드 생성기에 그대로 넣을 수 있습니다."
                    label="클라이언트 생성 도움말"
                  />
                </p>
                <CommandBox
                  command={`curl "${server}/api/v1/public/openapi.json" > signageone.json`}
                />
              </div>
            </div>
          </Panel>

          <Panel
            title="필요한 권한"
            help="토큰에는 필요한 최소 역할만 줍니다. 긴급 발령은 모든 화면을 즉시 덮으므로 system 으로 제한합니다."
          >
            <ul className="divide-y divide-slate-100">
              {roles.map((entry) => (
                <li key={entry.capability} className="flex items-baseline justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <code className="font-mono text-[12px] text-slate-700">{entry.capability}</code>
                    <p className="mt-0.5 text-[11.5px] text-slate-400">{entry.count}개 호출</p>
                  </div>
                  <Badge tone={entry.role === 'system' ? 'danger' : 'neutral'}>{entry.role}</Badge>
                </li>
              ))}
            </ul>
            <div className="border-t border-slate-100 px-4 py-3">
              <p className="flex gap-2 text-[12px] leading-relaxed text-slate-500">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} />
                <span>
                  긴급 발령만 <strong className="font-semibold text-slate-700">system</strong> 이 필요합니다. 공지
                  연동 토큰에는 <code className="font-mono">admin</code> 을 주세요.
                </span>
              </p>
            </div>
          </Panel>

          <Panel title="관련 문서">
            <ul className="divide-y divide-slate-100 text-[13px]">
              <li className="px-4 py-2.5">
                <a className="link" href="#/docs/public-api">
                  통합 API 안내서
                </a>
                <p className="mt-0.5 text-[11.5px] text-slate-400">연동 순서·주의 사항·체크리스트</p>
              </li>
              <li className="px-4 py-2.5">
                <a className="link" href="#/docs/api">
                  콘솔 API 레퍼런스
                </a>
                <p className="mt-0.5 text-[11.5px] text-slate-400">통합 API 에 없는 기능</p>
              </li>
            </ul>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

/**
 * 엔드포인트 한 줄.
 *
 * 접어 둔다. 12개를 모두 펼치면 훑는 것이 불가능해지고, 이 화면의 첫 용도는
 * "무엇이 있나" 를 보는 것이다. 자세한 것은 고른 하나만 본다.
 */
function EndpointRow({ endpoint, server }: { endpoint: Endpoint; server: string }): ReactNode {
  const [open, setOpen] = useState(false);
  const capability = endpoint.operation['x-capability'];
  const parameters = endpoint.operation.parameters ?? [];
  const bodySchema = endpoint.operation.requestBody?.content?.['application/json']?.schema;
  const responseSchema = responseSchemaOf(endpoint.operation);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-3 gap-y-1 px-5 py-3 text-left transition-colors hover:bg-slate-50 sm:flex-nowrap"
      >
        <span className="w-16 shrink-0">
          <Badge tone={METHOD_TONE[endpoint.method] ?? 'neutral'}>{endpoint.method}</Badge>
        </span>
        <code className="min-w-0 flex-1 font-mono text-[12.5px] text-slate-700 sm:flex-none">
          {endpoint.path.replace('/api/v1/public', '')}
        </code>
        {/*
          좁은 화면에서는 요약을 둘째 줄로 내린다. 경로가 한 줄을 다 쓰면 요약은 폭이 0 으로
          잘려 아예 보이지 않는다. 들여쓰기는 메서드 배지 폭(4rem)과 간격을 더한 만큼이다.
        */}
        <span className="order-last min-w-0 flex-1 basis-full truncate pl-[4.75rem] text-[12.5px] text-slate-500 sm:order-none sm:basis-0 sm:pl-0">
          {endpoint.operation.summary}
        </span>
        {capability ? (
          <code className="hidden shrink-0 font-mono text-[11.5px] text-slate-400 sm:inline">{capability}</code>
        ) : (
          <span className="hidden shrink-0 text-[11.5px] text-slate-400 sm:inline">인증 불필요</span>
        )}
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-300 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4">
          {/* 줄에서 감춘 권한 표기를 좁은 화면에서는 펼친 자리에 둔다 — 토큰 역할을 고르는 근거다. */}
          <p className="font-mono text-[11.5px] text-slate-400 sm:hidden">{capability ?? '인증 불필요'}</p>
          {endpoint.operation.description && (
            <div className="prose-tight text-[13px] leading-relaxed text-slate-600">
              <Markdown source={endpoint.operation.description} />
            </div>
          )}

          {parameters.length > 0 && <ParameterTable title="파라미터" parameters={parameters} />}
          {bodySchema && <FieldTable title="요청 본문" fields={fieldsOf(bodySchema)} />}
          {responseSchema && <FieldTable title="응답 (200)" fields={fieldsOf(responseSchema)} />}

          <div>
            <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-400">호출 예시</p>
            <CommandBox command={curlFor(endpoint, server)} />
          </div>
        </div>
      )}
    </li>
  );
}

function ParameterTable({ title, parameters }: { title: string; parameters: readonly OpenApiParameter[] }): ReactNode {
  return (
    <FieldTable
      title={title}
      fields={parameters.map((parameter) => ({
        name: parameter.in === 'path' ? `{${parameter.name}}` : parameter.name,
        type: typeLabel(parameter.schema),
        required: Boolean(parameter.required),
        description: parameter.description ?? '',
        constraint: constraintOf(parameter.schema),
      }))}
    />
  );
}

function FieldTable({ title, fields }: { title: string; fields: readonly Field[] }): ReactNode {
  if (fields.length === 0) return null;

  return (
    <div>
      <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.06em] text-slate-400">{title}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[30rem] text-[12.5px]">
          <tbody className="divide-y divide-slate-100">
            {fields.map((field) => (
              <tr key={field.name} className="align-top">
                <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-slate-800">
                  {field.name}
                  {field.required && <span className="ml-1 text-rose-500">*</span>}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-slate-500">{field.type}</td>
                <td className="px-3 py-2 text-slate-600">
                  {field.description && <Markdown source={field.description} />}
                  {field.constraint && (
                    <span className="mt-0.5 block text-[11.5px] text-slate-400">{field.constraint}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
